/**
 * Templates Selectors
 * Selectors for accessing template state
 */
import { createFeatureSelector, createSelector } from '@ngrx/store';
import { TemplatesState } from './templates.reducer';

// Feature selector
export const selectTemplatesState = createFeatureSelector<TemplatesState>('templates');

// User Templates Selectors
export const selectTemplates = createSelector(
  selectTemplatesState,
  (state) => state.templates
);

export const selectTemplatesTotal = createSelector(
  selectTemplatesState,
  (state) => state.templatesTotal
);

export const selectTemplatesPage = createSelector(
  selectTemplatesState,
  (state) => state.templatesPage
);

export const selectTemplatesPageSize = createSelector(
  selectTemplatesState,
  (state) => state.templatesPageSize
);

export const selectTemplatesTotalPages = createSelector(
  selectTemplatesState,
  (state) => state.templatesTotalPages
);

export const selectTemplatesHasNext = createSelector(
  selectTemplatesState,
  (state) => state.templatesHasNext
);

export const selectTemplatesHasPrev = createSelector(
  selectTemplatesState,
  (state) => state.templatesHasPrev
);

// Public Templates Selectors
export const selectPublicTemplates = createSelector(
  selectTemplatesState,
  (state) => state.publicTemplates
);

export const selectPublicTemplatesTotal = createSelector(
  selectTemplatesState,
  (state) => state.publicTemplatesTotal
);

export const selectPublicTemplatesPage = createSelector(
  selectTemplatesState,
  (state) => state.publicTemplatesPage
);

export const selectPublicTemplatesPageSize = createSelector(
  selectTemplatesState,
  (state) => state.publicTemplatesPageSize
);

export const selectPublicTemplatesTotalPages = createSelector(
  selectTemplatesState,
  (state) => state.publicTemplatesTotalPages
);

export const selectPublicTemplatesHasNext = createSelector(
  selectTemplatesState,
  (state) => state.publicTemplatesHasNext
);

export const selectPublicTemplatesHasPrev = createSelector(
  selectTemplatesState,
  (state) => state.publicTemplatesHasPrev
);

// Selected Template Selectors
export const selectSelectedTemplate = createSelector(
  selectTemplatesState,
  (state) => state.selectedTemplate
);

export const selectTestResult = createSelector(
  selectTemplatesState,
  (state) => state.testResult
);

// Filter Selectors
export const selectFilters = createSelector(
  selectTemplatesState,
  (state) => state.filters
);

// Loading State Selectors
export const selectLoading = createSelector(
  selectTemplatesState,
  (state) => state.loading
);

export const selectPublicTemplatesLoading = createSelector(
  selectTemplatesState,
  (state) => state.publicTemplatesLoading
);

export const selectSelectedTemplateLoading = createSelector(
  selectTemplatesState,
  (state) => state.selectedTemplateLoading
);

export const selectCreating = createSelector(
  selectTemplatesState,
  (state) => state.creating
);

export const selectUpdating = createSelector(
  selectTemplatesState,
  (state) => state.updating
);

export const selectDeleting = createSelector(
  selectTemplatesState,
  (state) => state.deleting
);

export const selectCloning = createSelector(
  selectTemplatesState,
  (state) => state.cloning
);

export const selectTesting = createSelector(
  selectTemplatesState,
  (state) => state.testing
);

// Error State Selectors
export const selectError = createSelector(
  selectTemplatesState,
  (state) => state.error
);

export const selectPublicTemplatesError = createSelector(
  selectTemplatesState,
  (state) => state.publicTemplatesError
);

export const selectSelectedTemplateError = createSelector(
  selectTemplatesState,
  (state) => state.selectedTemplateError
);

export const selectCreateError = createSelector(
  selectTemplatesState,
  (state) => state.createError
);

export const selectUpdateError = createSelector(
  selectTemplatesState,
  (state) => state.updateError
);

export const selectDeleteError = createSelector(
  selectTemplatesState,
  (state) => state.deleteError
);

export const selectCloneError = createSelector(
  selectTemplatesState,
  (state) => state.cloneError
);

export const selectTestError = createSelector(
  selectTemplatesState,
  (state) => state.testError
);

// Computed Selectors
export const selectActiveTemplates = createSelector(
  selectTemplates,
  (templates) => templates.filter(t => t.is_active)
);

export const selectInactiveTemplates = createSelector(
  selectTemplates,
  (templates) => templates.filter(t => !t.is_active)
);

export const selectTemplateById = (id: string) => createSelector(
  selectTemplates,
  (templates) => templates.find(t => t.id === id)
);

export const selectPublicTemplateById = (id: string) => createSelector(
  selectPublicTemplates,
  (templates) => templates.find(t => t.id === id)
);

// Combined Loading State
export const selectAnyLoading = createSelector(
  selectLoading,
  selectPublicTemplatesLoading,
  selectSelectedTemplateLoading,
  selectCreating,
  selectUpdating,
  selectDeleting,
  selectCloning,
  selectTesting,
  (loading, publicLoading, selectedLoading, creating, updating, deleting, cloning, testing) =>
    loading || publicLoading || selectedLoading || creating || updating || deleting || cloning || testing
);

// Pagination Info
export const selectTemplatesPaginationInfo = createSelector(
  selectTemplatesPage,
  selectTemplatesPageSize,
  selectTemplatesTotal,
  selectTemplatesTotalPages,
  selectTemplatesHasNext,
  selectTemplatesHasPrev,
  (page, pageSize, total, totalPages, hasNext, hasPrev) => ({
    page,
    pageSize,
    total,
    totalPages,
    hasNext,
    hasPrev
  })
);

export const selectPublicTemplatesPaginationInfo = createSelector(
  selectPublicTemplatesPage,
  selectPublicTemplatesPageSize,
  selectPublicTemplatesTotal,
  selectPublicTemplatesTotalPages,
  selectPublicTemplatesHasNext,
  selectPublicTemplatesHasPrev,
  (page, pageSize, total, totalPages, hasNext, hasPrev) => ({
    page,
    pageSize,
    total,
    totalPages,
    hasNext,
    hasPrev
  })
);

// ============================================================
// Template Selection Selectors (for API Usage)
// ============================================================

export const selectEnabledTemplateIds = createSelector(
  selectTemplatesState,
  (state) => state.enabledTemplateIds
);

export const selectEnablingTemplates = createSelector(
  selectTemplatesState,
  (state) => state.enablingTemplates
);

export const selectDisablingTemplates = createSelector(
  selectTemplatesState,
  (state) => state.disablingTemplates
);

export const selectEnableError = createSelector(
  selectTemplatesState,
  (state) => state.enableError
);

export const selectDisableError = createSelector(
  selectTemplatesState,
  (state) => state.disableError
);

// Check if a template is enabled for API usage
export const selectIsTemplateEnabled = (templateId: string) => createSelector(
  selectEnabledTemplateIds,
  (enabledIds) => enabledIds.includes(templateId)
);

// Get templates with their enabled status
export const selectTemplatesWithEnabledStatus = createSelector(
  selectTemplates,
  selectEnabledTemplateIds,
  (templates, enabledIds) => templates.map(t => ({
    ...t,
    isEnabledForApi: enabledIds.includes(t.id)
  }))
);

